import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Scale, Users, Dumbbell, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";

const fmt = (n) => "\u20ba" + new Intl.NumberFormat("tr-TR").format(n || 0);
const shortName = (n) => (n || "").replace(/^Get Up Fit\s*/i, "");
const FULL_ADMIN = ["admin", "super_admin", "franchise_owner"];

export default function BranchComparison() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const allowed = FULL_ADMIN.includes(user?.role);

  useEffect(() => { if (allowed) api.get("/reports/branches").then((r) => setRows(r.data)).catch(() => setRows([])); }, [allowed]);

  if (!allowed) return <Navigate to="/admin" replace />;
  if (rows === null) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl bg-[#13131C]" />)}</div>;

  const chartData = rows.map((b) => ({ name: shortName(b.branch_name), Ciro: b.month_revenue, Gider: b.month_expenses, Net: b.net }));
  const totals = rows.reduce((a, b) => ({
    members: a.members + b.total_members, trainers: a.trainers + b.total_trainers,
    appts: a.appts + b.month_appointments, net: a.net + b.net,
  }), { members: 0, trainers: 0, appts: 0, net: 0 });

  return (
    <div data-testid="admin-branch-comparison-page">
      <PageHeader title="Şube Karşılaştırma" subtitle="Tüm şubelerin performansını yan yana kıyasla" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Users} label="Toplam Üye" value={totals.members} />
        <Kpi icon={Dumbbell} label="Toplam Antrenör" value={totals.trainers} />
        <Kpi icon={CalendarClock} label="Bu Ay Randevu" value={totals.appts} />
        <Kpi icon={Scale} label="Toplam Net" value={fmt(totals.net)} accent />
      </div>

      <div className="rounded-2xl border border-[#222230] bg-[#13131C] p-4 mb-6" data-testid="branch-comparison-chart">
        <p className="text-sm font-semibold text-white mb-4">Bu Ay: Ciro / Gider / Net (₺)</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222230" />
            <XAxis dataKey="name" stroke="#68687B" fontSize={12} />
            <YAxis stroke="#68687B" fontSize={12} />
            <Tooltip contentStyle={{ background: "#181824", border: "1px solid #2D2D3F", borderRadius: 12, color: "#fff" }} formatter={(v) => fmt(v)} />
            <Bar dataKey="Ciro" fill="#FF5500" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Gider" fill="#68687B" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Net" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl border border-[#222230] overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="branch-comparison-table">
          <thead className="bg-[#181824] text-[#68687B] text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Şube</th>
              <th className="px-4 py-3 text-right">Üye</th>
              <th className="px-4 py-3 text-right hidden sm:table-cell">Aktif</th>
              <th className="px-4 py-3 text-right hidden md:table-cell">Antrenör</th>
              <th className="px-4 py-3 text-right hidden md:table-cell">Paket</th>
              <th className="px-4 py-3 text-right hidden lg:table-cell">Randevu (ay)</th>
              <th className="px-4 py-3 text-right">Ciro (ay)</th>
              <th className="px-4 py-3 text-right hidden sm:table-cell">Gider (ay)</th>
              <th className="px-4 py-3 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b, i) => (
              <tr key={b.branch_id} data-testid={`branch-row-${b.branch_id}`} className={`border-t border-[#1A1A24] ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                <td className="px-4 py-3 text-white font-medium">{shortName(b.branch_name)}</td>
                <td className="px-4 py-3 text-right text-white">{b.total_members}</td>
                <td className="px-4 py-3 text-right text-[#A0A0B2] hidden sm:table-cell">{b.active_members}</td>
                <td className="px-4 py-3 text-right text-[#A0A0B2] hidden md:table-cell">{b.total_trainers}</td>
                <td className="px-4 py-3 text-right text-[#A0A0B2] hidden md:table-cell">{b.total_packages}</td>
                <td className="px-4 py-3 text-right text-[#A0A0B2] hidden lg:table-cell">{b.month_appointments}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-[#FF5500]">{fmt(b.month_revenue)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#A0A0B2] hidden sm:table-cell">{fmt(b.month_expenses)}</td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${b.net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{fmt(b.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-[#222230] bg-[#13131C] p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#FF5500]/10 flex items-center justify-center"><Icon size={16} className="text-[#FF5500]" /></div>
      </div>
      <p className="text-[11px] uppercase tracking-wide text-[#68687B]">{label}</p>
      <p className={`text-xl font-bold ${accent ? "text-[#10B981]" : "text-white"}`}>{value}</p>
    </div>
  );
}
